import { useQueryClient } from "@tanstack/react-query";
import { Activity, AlertCircle, Radio, RefreshCw, Wallet } from "lucide-react";
import { formatAvailableBalance } from "@/components/billing/billing-format";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useRequiredOrgId } from "@/hooks/useRequiredOrgId";
import { queryKeys } from "@/lib/query-keys";
import { AnalyticsCharts } from "./components/AnalyticsCharts";
import { ApiKeyUsageTable } from "./components/ApiKeyUsageTable";
import { DateRangePicker } from "./components/DateRangePicker";
import { EndpointCards } from "./components/EndpointCards";
import {
  type AnalyticsReportParams,
  useAnalyticsReport,
} from "./hooks/useAnalyticsReport";
import {
  rollingRange,
  useAnalyticsViewState,
} from "./hooks/useAnalyticsViewState";
import {
  formatAudioHours,
  formatErrorRate,
  formatRequests,
} from "./utils/formatMetric";
import "./analytics.css";

/** Coordinates one consistently filtered report across summary, trends and endpoint breakdowns. */
export function AnalyticsDashboard() {
  const orgId = useRequiredOrgId();
  return <DashboardContent key={orgId} orgId={orgId} />;
}

function DashboardContent({ orgId }: { orgId: string }) {
  const client = useQueryClient();
  const [view, setView] = useAnalyticsViewState(orgId);
  const { presetDays, dateLabel, params, breakdown } = view;
  const setBreakdown = (breakdown: string) =>
    setView((current) => ({ ...current, breakdown }));
  const query = useAnalyticsReport(params);
  const report = query.data;
  const options = query.options ?? report?.options;
  const update = (patch: Partial<AnalyticsReportParams>) =>
    setView((current) => ({
      ...current,
      params: { ...current.params, ...patch },
    }));
  const refresh = () => {
    void client.invalidateQueries({
      queryKey: queryKeys.analytics.histories(orgId),
      refetchType: "none",
    });
    if (presetDays === null) void query.refetch();
    else update(rollingRange(presetDays));
  };
  const changeRange = ({
    from,
    to,
    label,
    presetDays,
  }: {
    from: Date;
    to: Date;
    label: string;
    presetDays: number | null;
  }) => {
    const nextDays = (to.getTime() - from.getTime()) / 86400000;
    setView((current) => ({
      ...current,
      presetDays,
      dateLabel: label,
      params: {
        ...current.params,
        from: from.toISOString(),
        to: to.toISOString(),
        granularity:
          nextDays <= 1 / 24 ? "minute" : nextDays <= 2 ? "hour" : "day",
      },
    }));
  };
  const overview = report?.overview;
  const total = overview?.totalRequests;
  const metrics = [
    {
      label: "Total requests",
      value: total === undefined ? "—" : formatRequests({ n: total }),
      icon: Activity,
    },
    {
      label: "Spend",
      value:
        report?.spend?.status === "ready"
          ? formatAvailableBalance(
              report.spend.totalAtoms,
              report.spend.currency,
            )
          : "Unavailable",
      icon: Wallet,
      unavailable: report?.spend?.status !== "ready",
    },
    {
      label: "HTTP error rate",
      value: overview ? formatErrorRate({ rate: overview.errorRate }) : "—",
      icon: AlertCircle,
    },
    {
      label: "Audio submitted",
      value: overview
        ? formatAudioHours({ seconds: overview.audioSecondsProcessed })
        : "—",
      icon: Radio,
    },
  ];
  return (
    <div className="dashboard-page analytics-page">
      <div className="page-heading">
        <div>
          <h1 className="page-title">Dashboard</h1>
        </div>
      </div>
      <section className="analytics-toolbar" aria-label="Analytics filters">
        <DateRangePicker
          from={params.from}
          to={params.to}
          label={dateLabel}
          onChange={changeRange}
        />
        <div className="analytics-filter-selects">
          <label>
            <span className="sr-only">API key</span>
            <select
              value={params.apiKeyId ?? ""}
              onChange={(e) =>
                update({ apiKeyId: e.target.value || undefined })
              }
            >
              <option value="">All API keys</option>
              {params.apiKeyId &&
                !options?.apiKeys.some((key) => key.id === params.apiKeyId) && (
                  <option value={params.apiKeyId}>Unavailable API key</option>
                )}
              {options?.apiKeys.map((key) => (
                <option key={key.id} value={key.id}>
                  {key.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Request type</span>
            <select
              value={params.requestType ?? ""}
              onChange={(e) =>
                update({ requestType: e.target.value || undefined })
              }
            >
              <option value="">All request types</option>
              {params.requestType &&
                !options?.requestTypes.includes(params.requestType) && (
                  <option value={params.requestType}>
                    {params.requestType}
                  </option>
                )}
              {options?.requestTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="sr-only">Status</span>
            <select
              value={params.status}
              onChange={(e) =>
                update({
                  status: e.target.value as AnalyticsReportParams["status"],
                })
              }
            >
              <option value="all">All statuses</option>
              <option value="success">Successful</option>
              <option value="4xx">4xx client errors</option>
              <option value="5xx">5xx server errors</option>
            </select>
          </label>
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label="Refresh analytics"
          disabled={query.isFetching}
          onClick={refresh}
        >
          <RefreshCw
            size={16}
            className={
              query.isFetching ? "animate-spin motion-reduce:animate-none" : ""
            }
          />
        </Button>
      </section>
      <div className="analytics-context">
        <output>
          {query.isFetching
            ? "Updating analytics…"
            : query.dataUpdatedAt
              ? `Updated ${new Date(query.dataUpdatedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
              : ""}
        </output>
      </div>
      {report?.truncated && (
        <Alert variant="destructive">
          <AlertDescription>
            Limited results: only the newest 10,000 log documents in this period
            were analyzed, before filtering. Totals and trends may be
            incomplete. Request history and exact request-ID search cover the
            full selected range. Choose a shorter date range.
          </AlertDescription>
        </Alert>
      )}
      {query.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {report
              ? `Analytics could not be updated. Showing data from ${new Date(query.dataUpdatedAt).toLocaleString()}. `
              : "Analytics could not be loaded. "}
            <Button variant="link" onClick={() => query.refetch()}>
              Try again
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {(!query.isError || report) && (
        <>
          <div className="metrics-summary analytics-summary">
            {metrics.map(({ label, value, icon: Icon, unavailable }) => (
              <Card key={label} className="stat-card rounded-none">
                <CardHeader className="stat-label px-0 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-[13px] font-medium text-muted-foreground">
                    {label}
                  </CardTitle>
                  <Icon
                    className="size-4 text-muted-foreground/75"
                    aria-hidden="true"
                  />
                </CardHeader>
                <CardContent className="stat-content px-0">
                  {query.isLoading ? (
                    <Skeleton className="h-8 w-24" />
                  ) : (
                    <div
                      className={
                        unavailable
                          ? "analytics-stat-unavailable"
                          : "stat-value"
                      }
                    >
                      {value}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          {report && (
            <>
              <AnalyticsCharts
                report={report}
                granularity={params.granularity}
              />
              <section
                className="analytics-breakdown"
                aria-label={
                  breakdown === "endpoints"
                    ? "Usage by endpoint"
                    : "Usage by API key"
                }
              >
                <div className="analytics-section-heading">
                  <h2>
                    {breakdown === "endpoints"
                      ? "Usage by endpoint"
                      : "Usage by API key"}
                  </h2>
                  <div className="analytics-tabs">
                    <Button
                      variant="ghost"
                      aria-pressed={breakdown === "endpoints"}
                      onClick={() => setBreakdown("endpoints")}
                    >
                      By endpoint
                    </Button>
                    <Button
                      variant="ghost"
                      aria-pressed={breakdown === "keys"}
                      onClick={() => setBreakdown("keys")}
                    >
                      By API key
                    </Button>
                  </div>
                </div>
                {breakdown === "endpoints" ? (
                  <EndpointCards report={report} params={params} />
                ) : (
                  <ApiKeyUsageTable data={report.apiKeys} isLoading={false} />
                )}
              </section>
            </>
          )}
          {query.isLoading && (
            <section
              className="analytics-charts"
              aria-label="Loading analytics"
            >
              <Skeleton className="h-80" />
              <Skeleton className="h-80" />
            </section>
          )}
        </>
      )}
    </div>
  );
}
